import * as Sentry from '@sentry/react'
import type { OrganizationAccessPlan } from '@/shared/hooks/api/organization'

export type OrganizationTrialInputFromApi = {
  trialTermsAcceptedAt: string | null
  trialEndsAt: string | null
  accessPlan: OrganizationAccessPlan
}

export type OrganizationAccess = {
  isTrialActive: boolean
  isTrialExpired: boolean
  canUseApp: boolean
  trialEndsAt: string | null
}

function trialWindowFromApi(args: {
  trialTermsAcceptedAt: string | null
  trialEndsAt: string | null
}): { trialFullyDefined: boolean; trialEndDate: Date | null } {
  const { trialTermsAcceptedAt, trialEndsAt } = args
  const trialStartPresent =
    trialTermsAcceptedAt != null && trialTermsAcceptedAt !== ''
  const trialEndDateRaw = trialEndsAt ? new Date(trialEndsAt) : null
  const trialEndPresent =
    trialEndDateRaw != null && !Number.isNaN(trialEndDateRaw.getTime())

  return {
    trialFullyDefined: trialStartPresent && trialEndPresent,
    trialEndDate: trialEndPresent ? trialEndDateRaw : null,
  }
}

function reportFreeTrialMissingWindow(extra: {
  trialTermsAcceptedAt: string | null
  trialEndsAt: string | null
}) {
  Sentry.captureException(
    new Error('FREE_TRIAL organization has no valid trial window'),
    {
      extra: {
        accessPlan: 'FREE_TRIAL',
        ...extra,
      },
    }
  )
}

export function computeOrganizationAccess(args: {
  organization: OrganizationTrialInputFromApi
  isArtemAdmin: boolean
  now: Date
}): OrganizationAccess {
  const { organization, isArtemAdmin, now } = args
  const { trialTermsAcceptedAt, trialEndsAt, accessPlan } = organization

  const { trialFullyDefined, trialEndDate } = trialWindowFromApi({
    trialTermsAcceptedAt,
    trialEndsAt,
  })

  const isTrialActive =
    trialFullyDefined &&
    trialEndDate != null &&
    now < trialEndDate

  const isTrialExpired =
    trialFullyDefined &&
    trialEndDate != null &&
    now >= trialEndDate

  if (accessPlan === 'FULL_ACCESS') {
    return {
      isTrialActive: false,
      isTrialExpired: false,
      canUseApp: true,
      trialEndsAt,
    }
  }

  if (accessPlan !== 'FREE_TRIAL') {
    Sentry.captureException(
      new Error('Unknown organization accessPlan'),
      {
        extra: {
          accessPlan,
          trialTermsAcceptedAt,
          trialEndsAt,
        },
      }
    )
    return {
      isTrialActive: false,
      isTrialExpired: false,
      canUseApp: isArtemAdmin,
      trialEndsAt,
    }
  }

  if (!trialFullyDefined) {
    reportFreeTrialMissingWindow({
      trialTermsAcceptedAt,
      trialEndsAt,
    })
    return {
      isTrialActive: false,
      isTrialExpired: false,
      canUseApp: isArtemAdmin,
      trialEndsAt,
    }
  }

  return {
    isTrialActive,
    isTrialExpired,
    canUseApp: isArtemAdmin || isTrialActive,
    trialEndsAt,
  }
}
